import { useEffect, useMemo, useState } from 'react';
import { Box, Skeleton, TextField, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckIcon from '@mui/icons-material/Check';
import BoltIcon from '@mui/icons-material/Bolt';
import AddIcon from '@mui/icons-material/Add';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import NotesIcon from '@mui/icons-material/Notes';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined';
import { useMaterialUIController } from 'context';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { formatCrmDateTime, formatCrmDayLabel, formatCrmTime } from '../../kanban-board/formatters/crm-datetime';
import { CrmHistoryService } from '../../kanban-board/services/crm-history.service';
import { LeadHistoryResponse } from '../../kanban-board/services/responses/lead-history.response';
import { LeadHistoryEventType } from '../../kanban-board/constants/crm-lead-history-event-type';
import { LeadBlock } from './lead-block';

interface Props {
    leadId: string;
    /// Muda quando algo acontece no lead, para o histórico recarregar.
    version: number;
    onChanged: () => void;
}

type HistoryFilter = 'tudo' | 'movimentacoes' | 'mensagens' | 'acoes' | 'automacoes';

const EVENT_FILTER: Record<number, HistoryFilter> = {
    [LeadHistoryEventType.Created]: 'movimentacoes',
    [LeadHistoryEventType.ColumnChanged]: 'movimentacoes',
    [LeadHistoryEventType.StatusChanged]: 'movimentacoes',
    [LeadHistoryEventType.AssignedChanged]: 'movimentacoes',
    [LeadHistoryEventType.TagsChanged]: 'movimentacoes',
    [LeadHistoryEventType.CustomFieldsChanged]: 'movimentacoes',
    [LeadHistoryEventType.MessageReceived]: 'mensagens',
    [LeadHistoryEventType.Comment]: 'mensagens',
    [LeadHistoryEventType.Email]: 'mensagens',
    [LeadHistoryEventType.ScheduleCreated]: 'acoes',
    [LeadHistoryEventType.ScheduleDone]: 'acoes',
    [LeadHistoryEventType.ScheduleReopened]: 'acoes',
    [LeadHistoryEventType.ScheduleRescheduled]: 'acoes',
    [LeadHistoryEventType.ScheduleNotReached]: 'acoes',
    [LeadHistoryEventType.AutomationApplied]: 'automacoes',
};

export const LeadHistoryWide = ({ leadId, version, onChanged }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [events, setEvents] = useState<LeadHistoryResponse[] | null>(null);
    const [filter, setFilter] = useState<HistoryFilter>('tudo');
    const [commentText, setCommentText] = useState('');

    useEffect(() => {
        setEvents(null);

        // O sufixo /paginated faz parte da rota: sem ele dá 404 e o histórico aparece vazio.
        new CrmHistoryService()
            .getAll(`${leadId}/paginated`, { currentPage: 1, recordsPerPage: 200 })
            .then((res) => setEvents(res.data.data?.records ?? []))
            .catch((err) => {
                setEvents([]);
                showAlert(apiErrorMessage(err), 'error', 4000);
            });
    }, [leadId, version]);

    const submitComment = async () => {
        const text = commentText.trim();

        if (!text) return;

        try {
            await new CrmHistoryService().create({ leadId, description: text });
            setCommentText('');
            onChanged();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        }
    };

    const markerFor = (eventType: number) => {
        const group = EVENT_FILTER[eventType] ?? 'movimentacoes';

        if (eventType === LeadHistoryEventType.MessageReceived)
            return { color: cc.eventSuccess, icon: <WhatsAppIcon sx={{ fontSize: 15 }} /> };
        if (eventType === LeadHistoryEventType.Email)
            return { color: cc.accent, icon: <MailOutlineIcon sx={{ fontSize: 15 }} /> };
        if (eventType === LeadHistoryEventType.ScheduleDone)
            return { color: cc.eventSuccess, icon: <CheckIcon sx={{ fontSize: 15 }} /> };
        if (group === 'acoes')
            return { color: cc.eventWarning, icon: <EventAvailableIcon sx={{ fontSize: 15 }} /> };
        if (group === 'automacoes')
            return { color: cc.textSecondary, icon: <BoltIcon sx={{ fontSize: 15 }} /> };
        if (eventType === LeadHistoryEventType.ColumnChanged)
            return { color: cc.accent, icon: <ArrowForwardIcon sx={{ fontSize: 15 }} /> };
        if (eventType === LeadHistoryEventType.Created)
            return { color: cc.textSecondary, icon: <AddIcon sx={{ fontSize: 15 }} /> };
        if (eventType === LeadHistoryEventType.AssignedChanged)
            return { color: cc.textSecondary, icon: <PersonAddAltIcon sx={{ fontSize: 15 }} /> };
        if (eventType === LeadHistoryEventType.TagsChanged)
            return { color: cc.accent, icon: <LocalOfferOutlinedIcon sx={{ fontSize: 15 }} /> };
        if (eventType === LeadHistoryEventType.CustomFieldsChanged)
            return { color: cc.textSecondary, icon: <DynamicFormOutlinedIcon sx={{ fontSize: 15 }} /> };

        return { color: cc.textSecondary, icon: <NotesIcon sx={{ fontSize: 15 }} /> };
    };

    /// O de→para em texto de gente.
    /// O servidor grava o nome do enum no FromValue do desfecho; a descrição do evento já diz o resto.
    const describeChange = (event: LeadHistoryResponse) => {
        // Nos eventos de ação o de→para é a data em ISO UTC, para a tela converter no fuso de quem lê.
        const isAction = EVENT_FILTER[event.eventType] === 'acoes';

        const label = (value?: string | null) => {
            if (!value) return undefined;

            if (isAction) return formatCrmDateTime(value);

            const outcome: Record<string, string> = {
                Open: t('desfecho.emAberto'),
                Won: t('desfecho.ganho'),
                Lost: t('desfecho.perdido'),
            };

            return outcome[value] ?? value;
        };

        // Desfecho: a descrição já diz "marcado como perdido"; repetir o estado anterior afasta o motivo.
        if (event.eventType === LeadHistoryEventType.StatusChanged) return label(event.toValue);

        const from = label(event.fromValue);
        const to = label(event.toValue);

        return from && to ? `${from} → ${to}` : to;
    };

    const visible = (events ?? []).filter(
        (event) => filter === 'tudo' || EVENT_FILTER[event.eventType] === filter);

    // Agrupa por dia: uma lista corrida de 200 eventos não diz quando as coisas aconteceram.
    const byDay = useMemo(() => {
        const groups = new Map<string, LeadHistoryResponse[]>();

        visible.forEach((event) => {
            const key = formatCrmDayLabel(event.createdAt);
            groups.set(key, [...(groups.get(key) ?? []), event]);
        });

        return [...groups.entries()];
    }, [visible]);

    const filters: { value: HistoryFilter; label: string }[] = [
        { value: 'tudo', label: t('telaLead.filtroTudo') },
        { value: 'movimentacoes', label: t('telaLead.filtroMovimentacoes') },
        { value: 'mensagens', label: t('telaLead.filtroMensagens') },
        { value: 'acoes', label: t('telaLead.filtroAcoes') },
        { value: 'automacoes', label: t('telaLead.filtroAutomacoes') },
    ];

    return (
        <LeadBlock
            title={t('telaLead.historico')}
            count={events ? t('telaLead.eventos', { total: events.length }) : undefined}
        >
            {/* Nota livre com autor e data: registra o que não veio de mensagem nem movimentação. */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                    size="small"
                    fullWidth
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submitComment();
                        }
                    }}
                    placeholder={t('telaLead.escreverNota')}
                    inputProps={{ maxLength: 500 }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: cc.surfaceBg,
                            borderRadius: '9px',
                            color: cc.textPrimary,
                            '& fieldset': { borderColor: cc.border },
                        },
                        '& .MuiInputBase-input': { color: cc.textPrimary, fontSize: 12.5 },
                    }}
                />
                <Box
                    role="button"
                    tabIndex={0}
                    onClick={submitComment}
                    onKeyDown={(e) => e.key === 'Enter' && submitComment()}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.9,
                        borderRadius: '9px',
                        fontSize: 12,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        cursor: commentText.trim() ? 'pointer' : 'default',
                        opacity: commentText.trim() ? 1 : 0.45,
                        color: cc.onTint,
                        border: `1px solid ${cc.accent}66`,
                        bgcolor: cc.accentSoftBg,
                    }}
                >
                    {t('telaLead.anotar')}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                {filters.map((item) => {
                    const active = filter === item.value;

                    return (
                        <Box
                            key={item.value}
                            role="button"
                            tabIndex={0}
                            onClick={() => setFilter(item.value)}
                            onKeyDown={(e) => e.key === 'Enter' && setFilter(item.value)}
                            sx={{
                                fontSize: 11.5,
                                fontWeight: active ? 700 : 600,
                                borderRadius: '999px',
                                px: 1.4,
                                py: 0.5,
                                cursor: 'pointer',
                                color: active ? cc.onTint : cc.textSecondary,
                                border: `1px solid ${active ? `${cc.accent}66` : cc.border}`,
                                bgcolor: active ? cc.accentSoftBg : 'transparent',
                            }}
                        >
                            {item.label}
                        </Box>
                    );
                })}
            </Box>

            {events === null ? (
                <Skeleton variant="rounded" height={180} />
            ) : visible.length === 0 ? (
                <Typography
                    variant="caption"
                    sx={{ color: cc.textMuted, display: 'block', py: 5, textAlign: 'center' }}
                >
                    {t('telaLead.semEventos')}
                </Typography>
            ) : (
                byDay.map(([day, dayEvents]) => (
                    <Box key={day}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.75, mb: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '.06em',
                                    textTransform: 'uppercase',
                                    color: cc.textMuted,
                                }}
                            >
                                {day}
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: cc.divider }} />
                        </Box>

                        {dayEvents.map((event) => {
                            const { color, icon } = markerFor(event.eventType);

                            // O detalhe só existe em quem tem: nota do
                            // agendamento, texto da mensagem, motivo da perda.
                            const detail = event.note;

                            const change = describeChange(event);

                            return (
                                <Box
                                    key={event.id}
                                    sx={{
                                        display: 'flex',
                                        gap: 1.5,
                                        p: 1.25,
                                        mb: 0.9,
                                        border: `1px solid ${cc.border}`,
                                        borderRadius: '10px',
                                        bgcolor: cc.surfaceBg,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: '8px',
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: `${color}26`,
                                            color,
                                        }}
                                    >
                                        {icon}
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Typography
                                                sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary }}
                                            >
                                                {event.description}
                                            </Typography>

                                            {event.userName && (
                                                <Typography sx={{ fontSize: 11, color: cc.textSecondary }}>
                                                    · {event.userName}
                                                </Typography>
                                            )}

                                            <Box sx={{ flex: 1 }} />

                                            <Typography sx={{ fontSize: 11, color: cc.textMuted }}>
                                                {formatCrmTime(event.createdAt)}
                                            </Typography>
                                        </Box>

                                        {change && (
                                            <Typography
                                                sx={{ fontSize: 11.5, color: cc.textSecondary, mt: 0.4 }}
                                            >
                                                {change}
                                            </Typography>
                                        )}

                                        {detail && (
                                            <Box
                                                sx={{
                                                    mt: 0.9,
                                                    p: 1.25,
                                                    borderRadius: '8px',
                                                    border: `1px solid ${cc.border}`,
                                                    borderLeft: `2px solid ${color}`,
                                                    bgcolor: cc.countBg,
                                                    fontSize: 12,
                                                    color: cc.textSecondary,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {detail}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ))
            )}
        </LeadBlock>
    );
};
